import { AccountSelectorNativeModal } from '@/design-system/components/accountSelector/AccountSelectorNativeModal';
import { createNativeStackNavigator, NativeStackScreenProps } from '@react-navigation/native-stack';
import * as DocumentPicker from 'expo-document-picker';
import * as React from 'react';
import { EarnStackRoutes } from '@/features/earn/earn.navigator';
import { OtpConfirmType, UnionType } from '@/features/bill-payment/enum';
import { PAYMENT_TYPE, QuoteDataRequest, QuoteResponse } from '@/features/bill-payment/hooks/useCreateBillPayment.hook';
import { BillPaymentConfirmationNavigator } from '@/features/bill-payment/screens/confirmation/BillPaymentConfirmation.navigator';
import { BillPaymentFormScreen } from '@/features/bill-payment/screens/form/BillPaymentForm.screen';
import { BillPaymentFormScheduleScreen } from '@/features/bill-payment/screens/form/BillPaymentFormSchedule.screen';
import { BillPaymentInstructionScreen } from '@/features/bill-payment/screens/instruction/BillPaymentInstruction.screen';
import { BillPaymentLandingScreen } from '@/features/bill-payment/screens/landing/BillPaymentLanding.screen';
import { OtpConfirmationNavigator } from '@/features/bill-payment/screens/otp/OtpConfirmation.navigator';
import { BillPaymentSuccessScreen } from '@/features/bill-payment/screens/success/BillPaymentSuccess.screen';
import { UnionPayNavigator } from '@/features/bill-payment/screens/union-pay/UnionPay.navigator';

export enum BillPaymentStackRoutes {
  Landing = 'bill-payment-landing',
  Instruction = 'bill-payment-instruction',
  Confirmation = 'bill-payment-confirmation',
  Form = 'bill-payment-form',
  FormSchedule = 'bill-payment-form-schedule',
  Success = 'bill-payment-success',
  OtpConfirmation = 'bill-payment-otp-confirmation',
  UnionPay = 'bill-payment-union-pay'
}

export interface BillPaymentProp {
  request: QuoteDataRequest;
  response: QuoteResponse;
  detailForm?: DetailFormRequest;
  documentSelected?: DocumentPicker.DocumentResult;
  paymentType: PAYMENT_TYPE;
}

export interface DetailFormRequest {
  bank: string;
  debitAccountWallerId: string;
  additionalDetail: string;
  assetName: string;
  amount: string;
  walletId: string;
  bankAccountNumber: string;
  debitAccountWalletName: string;
  remarks: string;
}

export interface BillPaymentConfirmProp {
  request: QuoteDataRequest;
  response: QuoteResponse;
  debitAccount: string;
  flagUploaded?: boolean;
  mode?: OtpConfirmType.SMS_OTP | OtpConfirmType.GOOGLE_OTP;
  documentSelected?: DocumentPicker.DocumentResult;
  paymentType: PAYMENT_TYPE;
}

export interface BillPaymentOTPConfirmProp extends BillPaymentConfirmProp {
  userFee: string;
}

export interface BillPaymentSuccessProp {
  request: QuoteDataRequest;
  response: QuoteResponse;
  debitAccount: string;
  userFee: string;
  submitResponse: {
    id: string;
    refId: string;
  };
  documentSelected?: DocumentPicker.DocumentResult;
  paymentType: PAYMENT_TYPE;
}

export interface BillPaymentUnionPay {
  unionType?: UnionType.PAYMENT | UnionType.ACCOUNT;
  refresh?: () => void;
  navigationNavigate?: string;
  navigationScreen?: string;
}

export type BillPaymentStackParamList = {
  [BillPaymentStackRoutes.Landing]: undefined;
  [BillPaymentStackRoutes.Instruction]: { paymentType: PAYMENT_TYPE };
  [BillPaymentStackRoutes.Confirmation]: BillPaymentConfirmProp;
  [BillPaymentStackRoutes.OtpConfirmation]: BillPaymentOTPConfirmProp;
  [BillPaymentStackRoutes.UnionPay]: BillPaymentUnionPay;
  [BillPaymentStackRoutes.Form]: BillPaymentProp;
  [BillPaymentStackRoutes.FormSchedule]: BillPaymentProp;
  [BillPaymentStackRoutes.Success]: BillPaymentSuccessProp;
  [EarnStackRoutes.AccountSelect]: {
    asset?: string;
    value?: string;
    disabledForwardBrowser?: boolean;
    onSelect: (walletId: string) => void;
  };
};

export type BillPaymentStackScreenProps<Screen extends keyof BillPaymentStackParamList> = NativeStackScreenProps<
  BillPaymentStackParamList,
  Screen
>;

const BillPaymentStack = createNativeStackNavigator<BillPaymentStackParamList>();

export function BillPaymentNavigator(): JSX.Element {
  return (
    <BillPaymentStack.Navigator
      screenOptions={{
        headerShown: false
      }}
      initialRouteName={BillPaymentStackRoutes.Landing}>
      <BillPaymentStack.Screen name={BillPaymentStackRoutes.Landing} component={BillPaymentLandingScreen} />
      <BillPaymentStack.Screen name={BillPaymentStackRoutes.Instruction} component={BillPaymentInstructionScreen} />
      <BillPaymentStack.Screen name={BillPaymentStackRoutes.Form} component={BillPaymentFormScreen} />
      <BillPaymentStack.Screen name={BillPaymentStackRoutes.FormSchedule} component={BillPaymentFormScheduleScreen} />
      <BillPaymentStack.Screen name={BillPaymentStackRoutes.Success} component={BillPaymentSuccessScreen} />
      <BillPaymentStack.Screen
        name={BillPaymentStackRoutes.Confirmation}
        component={BillPaymentConfirmationNavigator}
        options={{ presentation: 'transparentModal' }}
      />
      <BillPaymentStack.Screen
        name={EarnStackRoutes.AccountSelect}
        component={AccountSelectorNativeModal}
        options={{ presentation: 'transparentModal' }}
      />
      <BillPaymentStack.Screen
        name={BillPaymentStackRoutes.OtpConfirmation}
        component={OtpConfirmationNavigator}
        options={{ presentation: 'transparentModal' }}
      />
      <BillPaymentStack.Screen
        name={BillPaymentStackRoutes.UnionPay}
        component={UnionPayNavigator}
        options={{ presentation: 'transparentModal' }}
      />
    </BillPaymentStack.Navigator>
  );
}
